import {
  ApolloClient,
  ApolloLink,
  CombinedGraphQLErrors,
  InMemoryCache,
  ServerError,
  type TypePolicies,
} from "@apollo/client";
import { SetContextLink } from "@apollo/client/link/context";
import { ErrorLink } from "@apollo/client/link/error";
import { RemoveTypenameFromVariablesLink } from "@apollo/client/link/remove-typename";
import UploadHttpLink from "apollo-upload-client/UploadHttpLink.mjs";

const typePolicies: TypePolicies = {
  SceneDraft: {
    keyFields: false,
  },
  PerformerDraft: {
    keyFields: false,
  },
  Studio: {
    fields: {
      sub_studios: {
        keyArgs: ["input"],
      },
    },
  },
  Query: {
    fields: {
      findImage: {
        read(_, { args, toReference }) {
          return toReference({ __typename: "Image", id: args?.id });
        },
      },
      findStudio: {
        merge: true,
      },
    },
  },
};

const isDevEnvironment = () => import.meta.env.DEV;

export const getCredentialsSetting = () =>
  isDevEnvironment() && !import.meta.env.VITE_SERVER_URL
    ? "include"
    : "same-origin";

export const getPlatformURL = () => {
  let platformUrl = new URL(window.location.origin);

  if (isDevEnvironment()) {
    platformUrl = new URL(
      import.meta.env.VITE_SERVER_URL ?? window.location.origin,
    );
    platformUrl.port = import.meta.env.VITE_SERVER_PORT ?? "9998";
  }

  return platformUrl;
};

const httpLink = new UploadHttpLink({
  uri: `${getPlatformURL().toString().slice(0, -1)}/graphql`,
  fetchOptions: {
    mode: "cors",
    credentials: getCredentialsSetting(),
  },
});

const authLink = new SetContextLink(({ headers, ...context }) => ({
  headers: {
    ...headers,
    ...(import.meta.env.VITE_APIKEY && {
      ApiKey: import.meta.env.VITE_APIKEY,
    }),
  },
  ...context,
}));

const errorLink = new ErrorLink(({ error }) => {
  if (CombinedGraphQLErrors.is(error)) {
    error.errors.forEach(({ message }) => {
      console.log(`GraphQL error: ${message}`);
    });
  } else if (ServerError.is(error)) {
    console.log(`Server error: ${error.message}`);
  } else if (error) {
    console.log(`Other error: ${error.message}`);
  }
});

const createClient = () =>
  new ApolloClient({
    link: ApolloLink.from([
      authLink,
      errorLink,
      new RemoveTypenameFromVariablesLink(),
      httpLink,
    ]),
    cache: new InMemoryCache({ typePolicies }),
  });

export const setToken = (token: string) => {
  localStorage.setItem("token", token);
};

export default createClient;
